import { $enum } from "ts-enum-util";

import { AbsoluteRoute } from "../../core/routes/absoluteRoutes";

export enum Feature {
    home = "home",
    insights = "insights",
    // Keep after insights: getSelectedFeature returns the first FEATURES match, and /insights/monetization contains both.
    monetization = "monetization",
    lenses = "lenses",
    apis = "apis",
    communityAudience = "community-audience",
    welcome = "welcome",
    lensFolders = "lens-folders",
    assets = "assets",
    assetsCreate = "assets/create",
    apps = "apps",
    lensSchedulerGroups = "lens-scheduler/groups",
    lensSchedulerLenses = "lens-scheduler/lenses",
    lensSchedulerSources = "lens-scheduler/sources",
    lensScheduler = "lens-scheduler",
    cameraKit = "camera-kit",
    hostedLenses = "hosted-lenses",
    remoteAssets = "remote-assets",
    webLenses = "web-lenses",
    lensSubmit = "lens-submit",
}

export const HOME_FEATURES: readonly Feature[] = [Feature.lenses] as const;

export const FEATURES = $enum(Feature).getValues();

export const ALL_ORGS_FEATURES = [Feature.assets, Feature.hostedLenses, Feature.remoteAssets] as const;
export const LENS_FOLDER_FEATURES = [Feature.lenses, Feature.insights, Feature.assets, Feature.lensFolders] as const;

export const LENS_SCHEDULER_FEATURES: readonly Feature[] = [
    Feature.lensSchedulerGroups,
    Feature.lensSchedulerLenses,
    Feature.lensSchedulerSources,
    Feature.lensScheduler,
] as const;

export const NEW_USER_FEATURES: readonly Feature[] = [Feature.apis, Feature.cameraKit] as const;

export const CAMERAKIT_FEATURES = [...LENS_SCHEDULER_FEATURES, Feature.apps, Feature.cameraKit] as const;

/** Maps org-scoped features to their AbsoluteRoute (requiring only `{ orgId }`). */
const _FEATURE_ORG_ROUTES = {
    [Feature.lenses]: AbsoluteRoute.ORG_LENSES,
    [Feature.insights]: AbsoluteRoute.ORG_INSIGHTS,
    [Feature.monetization]: AbsoluteRoute.ORG_MONETIZATION,
    [Feature.lensFolders]: AbsoluteRoute.ORG_LENS_FOLDERS,
    [Feature.assets]: AbsoluteRoute.ORG_ASSETS,
    [Feature.assetsCreate]: AbsoluteRoute.ORG_ASSETS_CREATE,
    [Feature.apps]: AbsoluteRoute.ORG_APPS,
    [Feature.lensScheduler]: AbsoluteRoute.ORG_LENS_SCHEDULER,
    [Feature.lensSchedulerGroups]: AbsoluteRoute.ORG_LENS_SCHEDULER,
    [Feature.lensSchedulerLenses]: AbsoluteRoute.ORG_LENS_SCHEDULER,
    [Feature.lensSchedulerSources]: AbsoluteRoute.ORG_LENS_SCHEDULER,
    [Feature.hostedLenses]: AbsoluteRoute.ORG_HOSTED_LENSES,
    [Feature.remoteAssets]: AbsoluteRoute.ORG_REMOTE_ASSETS,
    [Feature.webLenses]: AbsoluteRoute.WEB_LENSES,
} as const;

type OrgRoute = (typeof _FEATURE_ORG_ROUTES)[keyof typeof _FEATURE_ORG_ROUTES];

export const FEATURE_ORG_ROUTE_MAP: Partial<Record<Feature, OrgRoute>> = _FEATURE_ORG_ROUTES;

const _LENS_FOLDER_FEATURE_ROUTES = {
    [Feature.insights]: AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS,
    [Feature.monetization]: AbsoluteRoute.ORG_LENS_FOLDER_MONETIZATION,
} as const;

type LensFolderRoute = (typeof _LENS_FOLDER_FEATURE_ROUTES)[keyof typeof _LENS_FOLDER_FEATURE_ROUTES];

export const LENS_FOLDER_FEATURE_ROUTE_MAP: Partial<Record<Feature, LensFolderRoute>> = _LENS_FOLDER_FEATURE_ROUTES;
